import type { ReactNode } from 'react'
import { CircleDashed } from 'lucide-react'
import type { PipelineStageDto, VenuePipelineDto } from '@lib/dto/pipeline.dto'
import type { PipelineCoupleList } from '@lib/constants/pipeline'
import { FUNNEL_MAIN_STEPS, FUNNEL_TARGET_LABELS, isFunnelMainStep, type FunnelStage, type FunnelTarget } from '@lib/enums/funnel-stage.enum'
import type { LeadStatsView } from '@lib/enums/lead-stats-view.enum'
import { couplesBefore } from '@lib/helpers/pipeline-story'
import { aboveUsualSentence, belowUsualSentence, touredNotBooked, type PipelineWhose } from '@lib/helpers/pipeline-text'
import { Button } from '@/components/ui/button'
import { Callout } from '@/components/ui/callout'
import { Card } from '@/components/ui/card'
import { wholePercent } from '@/components/stats/StatsPieces'
import { formatCount } from '@/lib/adFormat'
import { joinNames } from '@/lib/joinNames'
import { cn } from '@/lib/utils'

type FunnelData = Pick<VenuePipelineDto, 'stages' | 'view' | 'averageBookingCents' | 'lookAt'>

interface PipelineFunnelProps {
  data: FunnelData
  /** Opens the couples behind a count; without it, counts are plain numbers. */
  onOpen?: ((target: FunnelTarget) => void) | undefined
  /** Opens one of the lists worth a look; without it, the flag has no way to its couples. */
  onList?: ((list: PipelineCoupleList) => void) | undefined
  /** Whose average booking prices a shortfall: the venue's, or the company's. */
  whose?: 'venue' | 'company'
}

/** What each main step means, under its name. */
const HINTS: Readonly<Partial<Record<FunnelTarget, string>>> = {
  inquiry: 'Got in touch',
  qualified: 'Worth following up',
  tour_scheduled: 'Booked a tour',
  open_house_rsvp: 'Signed up for an open house',
  toured: 'Came to a tour or open house',
  booked: 'Signed and booked',
  event_day: 'Their day has passed',
}

function stageOf(stages: readonly PipelineStageDto[], target: FunnelTarget): PipelineStageDto | undefined {
  return stages.find((s) => s.target === target)
}

function Count({ stage, onOpen }: { stage: PipelineStageDto; onOpen?: ((target: FunnelTarget) => void) | undefined }) {
  const value = stage.value ?? 0
  const text = formatCount(value)
  // At least a short number's width, and as wide as a company's five digits need.
  const className = 'min-w-12 shrink-0 text-right font-mono text-stat-sm font-semibold tabular-nums text-ink'
  if (onOpen === undefined || value === 0) return <p className={className}>{text}</p>
  return (
    <button
      type="button"
      onClick={() => onOpen(stage.target)}
      aria-label={`See the ${text} couples at ${FUNNEL_TARGET_LABELS[stage.target]}`}
      className={cn(className, 'rounded-sm underline-offset-4 hover:text-accent-ink hover:underline focus-visible:focus-ds')}
    >
      {text}
    </button>
  )
}

/** A share on a track, with the usual by now as a tick across it. */
function Meter({ share, usual, tone }: { share: number | null; usual: number | null; tone: 'below' | 'above' | 'usual' }) {
  return (
    <div className={cn('relative h-1.5 rounded-xs', tone === 'below' ? 'bg-hold-soft' : 'bg-surface-sunken')}>
      {share === null ? null : (
        <div
          className={cn('h-1.5 rounded-xs', tone === 'below' && 'bg-hold-ink', tone === 'above' && 'bg-ok-ink', tone === 'usual' && 'bg-ink-muted')}
          style={{ width: `${String(Math.min(100, share * 100))}%` }}
        />
      )}
      {usual === null ? null : <div aria-hidden className="absolute -top-1 h-3.5 w-0.5 bg-ink" style={{ left: `${String(Math.min(100, usual * 100))}%` }} />}
    </div>
  )
}

/**
 * Who a share is out of: the couples at the step before, or, where the
 * venue's timing isn't known yet, those of them old enough to judge.
 */
function ofWhom(stage: PipelineStageDto): string {
  const u = stage.usual
  if (u?.settledOnly === true) return `of the ${formatCount(u.whole)} old enough to judge`
  return `of ${couplesBefore(stage).replace(/^the /, '')}`
}

/** What's usual, said under the share: by now, once everyone decides, and who is still deciding. */
function usualText(stage: PipelineStageDto): string {
  const u = stage.usual
  if (u === null) {
    // No couples at the step before is not the same as no history to judge them by.
    return stage.shareOfPrevious === null && stage.before.length > 0 ? 'No couples at the step before yet' : 'Not enough history to say what’s usual yet'
  }
  const parts = [`Usual by now ${wholePercent(u.byNow)}`]
  if (Math.abs(u.share - u.byNow) >= 0.02) parts.push(`${wholePercent(u.share)} once all decide`)
  if (u.stillDeciding > 0) parts.push(`${formatCount(u.stillDeciding)} still deciding`)
  return parts.join(' · ')
}

/** A main step's share of the step before, against what's usual; nothing in *what happened*, which counts steps, not couples. */
function Rate({ stage, view }: { stage: PipelineStageDto; view: LeadStatsView }) {
  if (stage.target === 'inquiry') return <p className="m-0 text-small text-ink-muted">Where the funnel starts</p>
  // Most booked couples' days are still ahead, so a share of them would read as a shortfall.
  if (stage.target === 'event_day') return <p className="m-0 text-small text-ink-muted">Counts once their day has passed</p>
  if (view !== 'cohort') return <span />
  const u = stage.usual
  const share = u === null ? stage.shareOfPrevious : u.part / u.whole
  const tone = u?.verdict ?? 'usual'
  return (
    <div className="space-y-1.5">
      <p className="m-0 flex items-baseline justify-between gap-2">
        <span className={cn('font-mono text-sm font-semibold tabular-nums', tone === 'below' ? 'text-hold-ink' : 'text-ink')}>
          {share === null ? '—' : wholePercent(share)}
        </span>
        <span className="text-right text-xs text-ink-muted">{ofWhom(stage)}</span>
      </p>
      <Meter share={share} usual={u?.byNow ?? null} tone={tone} />
      <p className="m-0 text-xs text-ink-muted">{usualText(stage)}</p>
    </div>
  )
}

/** What a step below or above usual means, in couples and, for bookings, in what they're worth: worded as the email words it. */
function Flag({
  stage,
  data,
  onList,
  whose,
}: {
  stage: PipelineStageDto
  data: FunnelData
  onList?: ((list: PipelineCoupleList) => void) | undefined
  whose: PipelineWhose
}) {
  if (!isFunnelMainStep(stage.target)) return null
  const above = aboveUsualSentence(stage)
  if (above !== null) return <p className="m-0 text-small text-ok-ink md:col-span-3">{above}</p>
  const below = belowUsualSentence(stage, data.averageBookingCents, whose)
  if (below === null) return null
  // Straight to the couples there are: the lost ones when nobody is still deciding.
  const behind = stage.target === 'booked' ? touredNotBooked(data.lookAt) : null
  const action =
    behind !== null && onList !== undefined ? (
      <Button variant="outline" size="sm" onClick={() => onList(behind.list)}>
        See the {formatCount(behind.count)} who toured
      </Button>
    ) : undefined
  // The way to the couples goes under the sentence, so a phone keeps the sentence its width.
  return (
    <Callout tone="attention" title="Below usual" className="md:col-span-3">
      <p className="m-0">{below}</p>
      {action === undefined ? null : <div className="mt-2">{action}</div>}
    </Callout>
  )
}

function StepRow({ stage, children }: { stage: PipelineStageDto; children: ReactNode }) {
  return (
    <li className="grid grid-cols-1 gap-x-5 gap-y-2 border-t border-line py-3 md:grid-cols-[10rem_minmax(0,1fr)_15rem] md:items-center">
      <div>
        <p className="m-0 text-sm font-medium text-ink">{FUNNEL_TARGET_LABELS[stage.target]}</p>
        <p className="m-0 text-small text-ink-muted">{HINTS[stage.target] ?? ''}</p>
      </div>
      {children}
    </li>
  )
}

/** What the steps along the way are: counted, not dividing; and, for couples who inquired, what Booked is measured against instead. */
function alongNote(base: FunnelStage | undefined, view: LeadStatsView): string {
  if (view !== 'cohort' || base === undefined) return 'Counted, but they don’t divide the funnel.'
  return `Counted, but they don’t divide the funnel: Booked is measured against ${FUNNEL_TARGET_LABELS[base]}.`
}

/** The steps couples may skip, counted beside the funnel rather than dividing it. */
function AlongTheWay({
  stages,
  booked,
  view,
  onOpen,
}: {
  stages: PipelineStageDto[]
  booked: PipelineStageDto | undefined
  view: LeadStatsView
  onOpen?: ((target: FunnelTarget) => void) | undefined
}) {
  if (stages.length === 0) return null
  const base = booked?.before[0]
  return (
    <li className="grid grid-cols-1 gap-x-5 gap-y-2 border-t border-dashed border-line py-3 md:grid-cols-[10rem_minmax(0,1fr)_15rem]">
      <div>
        <p className="m-0 text-sm font-medium text-ink-muted">Along the way</p>
        <p className="m-0 text-small text-ink-muted">Steps couples may skip</p>
      </div>
      <div className="space-y-2 md:col-span-2">
        <ul aria-label="Steps along the way" className="m-0 flex list-none flex-wrap gap-2 p-0">
          {stages.map((s) => (
            <li key={s.target}>
              <AlongChip stage={s} onOpen={onOpen} />
            </li>
          ))}
        </ul>
        <p className="m-0 text-xs text-ink-muted">
          {alongNote(base, view)}
        </p>
      </div>
    </li>
  )
}

function AlongChip({ stage, onOpen }: { stage: PipelineStageDto; onOpen?: ((target: FunnelTarget) => void) | undefined }) {
  const value = stage.value ?? 0
  const content = (
    <>
      {FUNNEL_TARGET_LABELS[stage.target]} <span className="font-mono font-semibold tabular-nums">{formatCount(value)}</span>
    </>
  )
  const chip = 'inline-flex h-8 items-center gap-2 rounded-md border border-line bg-surface px-2.5 text-control text-ink'
  if (onOpen === undefined || value === 0) return <span className={chip}>{content}</span>
  return (
    <button
      type="button"
      onClick={() => onOpen(stage.target)}
      aria-label={`See the ${formatCount(value)} couples at ${FUNNEL_TARGET_LABELS[stage.target]}`}
      className={cn(chip, 'hover:bg-surface-hover focus-visible:focus-ds')}
    >
      {content}
    </button>
  )
}

/**
 * Where couples drop off (docs/pipeline-view.spec.md, "Main steps"): the main
 * steps in order, each with its share of the step before against what's usual
 * by now, a flag where it's below and what that costs; the steps along the way
 * beside them; what nothing here records, in a line.
 */
export function PipelineFunnel({ data, onOpen, onList, whose = 'venue' }: PipelineFunnelProps) {
  const tracked = data.stages.filter((s) => s.tracked)
  const main = FUNNEL_MAIN_STEPS.flatMap((target) => tracked.filter((s) => s.target === target))
  const along = tracked.filter((s) => !isFunnelMainStep(s.target) && s.target !== 'lost' && s.target !== 'canceled')
  const notTracked = data.stages.filter((s) => !s.tracked && s.target !== 'lost' && s.target !== 'canceled').map((s) => FUNNEL_TARGET_LABELS[s.target])
  const booked = stageOf(data.stages, 'booked')
  // Couples narrow from the inquiries; steps taken in the weeks don't, so they're set against the largest.
  const scale = data.view === 'cohort' ? (stageOf(data.stages, 'inquiry')?.value ?? 0) : Math.max(0, ...main.map((s) => s.value ?? 0))
  const lost = stageOf(data.stages, 'lost')
  const canceled = stageOf(data.stages, 'canceled')

  return (
    <Card asChild>
      <section id="funnel" aria-labelledby="funnel-heading" className="space-y-3 p-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="space-y-0.5">
            <h2 id="funnel-heading" className="m-0 text-heading font-semibold text-ink">
              Where couples drop off
            </h2>
            <p className="m-0 text-small text-ink-muted">
              {data.view === 'cohort'
                ? 'Each step’s share is of the couples at the step before, set against what’s usual here by now.'
                : 'How many couples took each step in these weeks, whenever they inquired.'}
            </p>
          </div>
          {data.view === 'cohort' ? (
            <div className="flex flex-wrap gap-3 text-xs text-ink-muted">
              <span className="inline-flex items-center gap-1.5">
                <span aria-hidden className="inline-block h-1.5 w-3.5 rounded-xs bg-ink-muted" />
                These couples
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span aria-hidden className="inline-block h-3 w-0.5 bg-ink" />
                Usual by now
              </span>
            </div>
          ) : null}
        </div>
        <ol aria-label="Steps from inquiry to the event" className="m-0 list-none p-0">
          {main.map((stage) => (
            <StepFragment
              key={stage.target}
              stage={stage}
              data={data}
              scale={scale}
              along={stage.target === 'booked' ? along : []}
              onOpen={onOpen}
              onList={onList}
              whose={whose}
            />
          ))}
          {booked?.tracked === true ? null : <AlongTheWay stages={along} booked={booked} view={data.view} onOpen={onOpen} />}
        </ol>
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 border-t border-line pt-3 text-small text-ink-muted">
          {notTracked.length === 0 ? (
            <span />
          ) : (
            <p className="m-0 flex items-center gap-2">
              <CircleDashed aria-hidden className="h-4 w-4 shrink-0" />
              <span>
                Not tracked here: {joinNames(notTracked)}.{' '}
                <a href="#tracking" className="font-medium text-accent-ink hover:underline">
                  How to track them
                </a>
              </span>
            </p>
          )}
          <p className="m-0 flex gap-4">
            {lost?.tracked === true ? <OutcomeCount stage={lost} onOpen={onOpen} /> : null}
            {canceled?.tracked === true ? <OutcomeCount stage={canceled} onOpen={onOpen} /> : null}
          </p>
        </div>
      </section>
    </Card>
  )
}

function OutcomeCount({ stage, onOpen }: { stage: PipelineStageDto; onOpen?: ((target: FunnelTarget) => void) | undefined }) {
  const value = stage.value ?? 0
  const label = FUNNEL_TARGET_LABELS[stage.target]
  if (onOpen === undefined || value === 0) {
    return (
      <span>
        {label} <span className="font-mono font-semibold tabular-nums text-ink">{formatCount(value)}</span>
      </span>
    )
  }
  return (
    <button
      type="button"
      onClick={() => onOpen(stage.target)}
      aria-label={`See the ${formatCount(value)} couples at ${label}`}
      className="rounded-sm hover:text-ink hover:underline focus-visible:focus-ds"
    >
      {label} <span className="font-mono font-semibold tabular-nums text-ink">{formatCount(value)}</span>
    </button>
  )
}

/** A main step's row, the steps along the way just before Booked, and its flag. */
function StepFragment({
  stage,
  data,
  scale,
  along,
  onOpen,
  onList,
  whose,
}: {
  stage: PipelineStageDto
  data: FunnelData
  scale: number
  along: PipelineStageDto[]
  onOpen?: ((target: FunnelTarget) => void) | undefined
  onList?: ((list: PipelineCoupleList) => void) | undefined
  whose: 'venue' | 'company'
}) {
  const booked = stage.target === 'booked' ? stage : undefined
  const width = scale > 0 ? Math.min(100, ((stage.value ?? 0) / scale) * 100) : 0
  return (
    <>
      {booked === undefined ? null : <AlongTheWay stages={along} booked={booked} view={data.view} onOpen={onOpen} />}
      <StepRow stage={stage}>
        <div className="flex items-center gap-3">
          <div className="h-6 flex-1 overflow-hidden rounded-sm bg-surface-sunken">
            <div className="h-full rounded-sm bg-primary" style={{ width: `${String(width)}%` }} />
          </div>
          <Count stage={stage} onOpen={onOpen} />
        </div>
        <Rate stage={stage} view={data.view} />
        <Flag stage={stage} data={data} onList={onList} whose={whose} />
      </StepRow>
    </>
  )
}
